import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaFileContract, FaChevronDown, FaExclamationTriangle } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const sections = [
    {
        title: "Acceptance of Terms",
        badge: "Important",
        badgeColor: "bg-red-100 text-red-600",
        content: [
            {
                heading: "Agreement to Terms",
                text: "By accessing or using DreamToday, you confirm that you are at least 18 years of age and agree to be bound by these Terms and Conditions. If you do not agree, please do not use the platform.",
            },
            {
                heading: "Updates to Terms",
                text: "DreamToday reserves the right to update these Terms at any time. Continued use of the platform after changes constitutes acceptance of the revised Terms. We will notify users of material changes via email.",
            },
        ],
    },
    {
        title: "Eligibility",
        content: [
            {
                heading: "Age Requirement",
                text: "You must be 18 years or older to register and participate on DreamToday. By registering, you represent and warrant that you meet this age requirement.",
            },
            {
                heading: "Geographic Restrictions",
                text: "DreamToday is currently available to residents of India. Users from jurisdictions where participation in such platforms is prohibited are not permitted to use the service.",
            },
            {
                heading: "One Account Per User",
                text: "Each user is permitted to maintain only one account. Creating multiple accounts to gain unfair advantages will result in immediate termination of all associated accounts.",
            },
        ],
    },
    {
        title: "Account Registration & Security",
        content: [
            {
                heading: "Accurate Information",
                text: "You agree to provide accurate, current, and complete information during registration. Providing false information is grounds for immediate account suspension.",
            },
            {
                heading: "Account Security",
                text: "You are responsible for maintaining the confidentiality of your login credentials. You agree to notify us immediately of any unauthorized access to your account.",
            },
            {
                heading: "Account Termination",
                text: "DreamToday reserves the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or misuse the platform.",
            },
        ],
    },
    {
        title: "Coins, Wallet & Payments",
        content: [
            {
                heading: "Coin Purchases",
                text: "Coins are virtual credits used exclusively within the DreamToday platform. All coin purchases are final and non-refundable unless required by applicable law.",
            },
            {
                heading: "Coin Expiry",
                text: "Coins purchased on DreamToday expire 60 days from the date of purchase. Expired coins are forfeited and no refund or extension will be provided.",
            },
            {
                heading: "Pricing",
                text: "All prices displayed on the platform are inclusive of applicable taxes. DreamToday reserves the right to change coin package pricing at any time without prior notice.",
            },
            {
                heading: "Failed Transactions",
                text: "In case of a failed transaction where coins were not credited, contact support within 48 hours with proof of payment. We will investigate and resolve the issue promptly.",
            },
        ],
    },
    {
        title: "Campaign Participation",
        content: [
            {
                heading: "Ticket Purchases",
                text: "Ticket purchases are final and non-refundable. By purchasing a ticket, you acknowledge that winning is not guaranteed and is subject to a fair random campaign.",
            },
            {
                heading: "Campaign Rules",
                text: "Each campaign may have specific terms and conditions. By participating, you agree to the rules of the individual campaign in addition to these general Terms.",
            },
            {
                heading: "Ticket Limits",
                text: "DreamToday may impose maximum ticket limits per user per campaign to ensure fairness. Attempting to circumvent these limits will result in account suspension.",
            },
        ],
    },
    {
        title: "Campaign & Winner Selection",
        content: [
            {
                heading: "Fair & Random Campaign",
                text: "All campaigns are conducted using a certified random selection process and streamed live to ensure transparency. DreamToday employees and their immediate family members are not eligible to participate.",
            },
            {
                heading: "Winner Notification",
                text: "Winners will be notified via the registered email and phone number within 24 hours of the campaign. Failure to respond within 7 days of notification may result in forfeiture of the prize.",
            },
            {
                heading: "Campaign Postponement",
                text: "DreamToday reserves the right to postpone or reschedule a campaign in exceptional circumstances. Ticket holders will be notified of any changes.",
            },
        ],
    },
    {
        title: "Prize Delivery",
        content: [
            {
                heading: "Delivery Terms",
                text: "Prizes will be delivered to the winner's registered address within India. DreamToday is not responsible for delays caused by courier services or incorrect address information provided by the winner.",
            },
            {
                heading: "Prize Acceptance",
                text: "Prizes are non-transferable and cannot be exchanged for cash or any other item. Winners must accept prizes as offered.",
            },
            {
                heading: "Taxes",
                text: "Winners are solely responsible for any taxes, duties, or levies applicable to prizes received. DreamToday will provide necessary documentation for tax purposes as required by law.",
            },
        ],
    },
    {
        title: "Prohibited Conduct",
        badge: "Critical",
        badgeColor: "bg-orange-100 text-orange-600",
        content: [
            {
                heading: "Fraudulent Activity",
                text: "Any attempt to manipulate campaigns, exploit platform vulnerabilities, use bots, or engage in any form of fraud is strictly prohibited and may result in legal action.",
            },
            {
                heading: "Abuse & Harassment",
                text: "Users must not harass, threaten, or abuse other users, staff, or winners. Violations will result in immediate account termination.",
            },
            {
                heading: "Misrepresentation",
                text: "You may not impersonate any person or entity, or falsely claim affiliation with any organization in connection with DreamToday.",
            },
        ],
    },
    {
        title: "Limitation of Liability",
        content: [
            {
                heading: "No Warranty",
                text: "DreamToday is provided 'as is' without warranties of any kind. We do not guarantee uninterrupted, error-free access to the platform.",
            },
            {
                heading: "Liability Cap",
                text: "To the maximum extent permitted by law, DreamToday's total liability for any claim arising from use of the platform shall not exceed the amount paid by you in the 30 days preceding the claim.",
            },
            {
                heading: "Indirect Damages",
                text: "DreamToday shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of or inability to use the platform.",
            },
        ],
    },
    {
        title: "Governing Law & Disputes",
        content: [
            {
                heading: "Governing Law",
                text: "These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Hyderabad, Telangana.",
            },
            {
                heading: "Dispute Resolution",
                text: "We encourage users to contact our support team first to resolve disputes amicably. If unresolved, disputes shall be settled through arbitration as per the Arbitration and Conciliation Act, 1996.",
            },
        ],
    },
];

const AccordionItem = ({ section, index, isOpen, onToggle }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.04 }}
        className={`rounded-2xl border overflow-hidden transition-all duration-300 ${isOpen ? "border-yellow-400 shadow-md" : "border-yellow-100"
            }`}
    >
        <button
            onClick={onToggle}
            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left
        bg-white/80 hover:bg-yellow-50/60 transition"
        >
            <div className="flex items-center gap-3 flex-wrap">
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500
          flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm">
                    {index + 1}
                </span>
                <span className={`font-semibold text-sm sm:text-base ${isOpen ? "text-yellow-700" : "text-gray-800"}`}>
                    {section.title}
                </span>
                {section.badge && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${section.badgeColor}`}>
                        {section.badge}
                    </span>
                )}
            </div>
            <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0">
                <FaChevronDown className={`text-sm ${isOpen ? "text-yellow-500" : "text-gray-400"}`} />
            </motion.div>
        </button>

        {isOpen && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-6 pb-6 pt-2 bg-gradient-to-b from-yellow-50/40 to-white/60 border-t border-yellow-100 space-y-4"
            >
                {section.content.map((item, i) => (
                    <div key={i}>
                        <p className="font-semibold text-gray-800 text-sm mb-1">{item.heading}</p>
                        <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
                    </div>
                ))}
            </motion.div>
        )}
    </motion.div>
);

const TermsAndConditions = () => {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-b from-white via-[#FFF7DB] to-white pt-28 pb-20 px-4 relative overflow-hidden">

                {/* BG BLOBS */}
                <div className="absolute top-0 left-0 w-80 h-80 rounded-full
          bg-gradient-to-br from-yellow-100 to-amber-50 opacity-40 blur-3xl -translate-x-1/3 -translate-y-1/4 pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full
          bg-gradient-to-br from-amber-100 to-yellow-50 opacity-30 blur-3xl translate-x-1/3 translate-y-1/4 pointer-events-none" />

                <div className="max-w-4xl mx-auto relative z-10">

                    {/* HERO */}
                    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-14">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500
              flex items-center justify-center shadow-lg mx-auto mb-5">
                            <FaFileContract className="text-white text-2xl" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold mb-3
              bg-gradient-to-r from-yellow-600 via-yellow-700 to-yellow-800 bg-clip-text text-transparent">
                            Terms & Conditions
                        </h1>
                        <p className="text-gray-500 text-sm">Last Updated: March 05, 2026</p>
                        <p className="text-gray-500 max-w-xl mx-auto mt-3 text-base">
                            Please read these Terms carefully before using DreamToday. They govern your use of our platform and participation in campaigns.
                        </p>
                    </motion.div>

                    {/* WARNING BANNER */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 mb-10 flex items-start gap-4"
                    >
                        <FaExclamationTriangle className="text-amber-500 text-xl shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-600 leading-relaxed">
                            These Terms constitute a legally binding agreement between you and DreamToday. By creating an account or using any part of the platform, you acknowledge that you have read, understood, and agreed to these Terms.
                        </p>
                    </motion.div>

                    {/* ACCORDION */}
                    <div className="space-y-3">
                        {sections.map((section, i) => (
                            <AccordionItem
                                key={i}
                                section={section}
                                index={i}
                                isOpen={openIndex === i}
                                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                            />
                        ))}
                    </div>

                    {/* FOOTER */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="mt-14 text-center bg-gradient-to-r from-yellow-50 to-amber-50
              border border-yellow-200 rounded-3xl p-8"
                    >
                        <h3 className="text-lg font-bold text-gray-800 mb-2">Need clarification?</h3>
                        <p className="text-gray-500 text-sm mb-5">
                            If you have questions about these Terms, reach out to our legal team at{" "}
                            <a href="mailto:legal@dreamtoday.com" className="text-yellow-600 font-medium hover:underline">
                                legal@dreamtoday.com
                            </a>
                        </p>
                        <div className="flex flex-wrap justify-center gap-3">
                            <a href="/contactus"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white
                  bg-gradient-to-r from-yellow-500 to-amber-600 shadow-md hover:scale-105 transition text-sm">
                                Contact Us
                            </a>
                            <a href="/privacy"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-yellow-700
                  border border-yellow-300 hover:bg-yellow-50 transition text-sm">
                                Privacy Policy
                            </a>
                        </div>
                    </motion.div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default TermsAndConditions;